import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import type { SelectChangeEvent } from '@liquidweb-dev/undercurrent/Select';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';

import Box from '@liquidweb-dev/undercurrent/Box';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import Select from '@liquidweb-dev/undercurrent/Select';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import Settings2Icon from '@liquidweb-dev/undercurrent/icons/lucide/Settings2';
import ToolboxIcon from '@liquidweb-dev/undercurrent/icons/lucide/lab/Toolbox';
import FlagIcon from '@liquidweb-dev/undercurrent/icons/lucide/Flag';
import ContainerIcon from '@liquidweb-dev/undercurrent/icons/lucide/Container';
import { useColorScheme } from '@liquidweb-dev/undercurrent/styles';

import useAccountDetails from 'modules/queries/account/useDetails';
import { selectors as appConfigSelectors } from 'modules/appConfig';
import { useDevTheme } from 'contexts/DevThemeContext';
import type { LWApiPublicAccountSupportSignupParamsRaw } from '@liquidweb-dev/club-sauce/public/account/index.raw';

type Brand = LWApiPublicAccountSupportSignupParamsRaw['brand'];

const HeadsUpDisplay = () => {
	const appConfig = useSelector(appConfigSelectors.getAppConfig);
	const { brand, setBrand } = useDevTheme();
	const { mode, setMode } = useColorScheme();
	const { user } = useAuth();

	const brandValue = brand ?? 'liquidweb';

	const [isDevToolsOpen, setIsDevToolsOpen] = useState(false);

	const { data: accountDetails } = useAccountDetails(
		{},
		{
			// Just rely on data in existing cache since this only renders in
			// non-prod envs. No need to make an extra api call.
			enabled: false,
		},
	);

	if (!appConfig?.headsUpDisplay) return null;

	const env = process.env.REACT_APP_CONFIG || 'local';

	const userEmail = user?.profile?.email;

	const accountNumber = accountDetails?.accnt;

	const handleBrandChange = (event: SelectChangeEvent<Brand>) => {
		setBrand(event.target.value as Brand);
	};

	const handleThemeChange = (
		event: SelectChangeEvent<'light' | 'dark' | 'system'>,
	) => {
		setMode(event.target.value);
	};

	return (
		<>
			<Box
				component="aside"
				aria-label="Heads-up display"
				sx={({ vars }) => ({
					position: 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					zIndex: 1300,
					bgcolor: vars.palette.uc.bg.main,
					borderTop: `1px solid ${vars.palette.uc.border.weak}`,
					px: 200,
					py: 50,
				})}
			>
				<Stack
					direction="row"
					sx={{
						gap: 300,
						alignItems: 'center',
					}}
				>
					<Stack
						direction="row"
						sx={{
							gap: 100,
							alignItems: 'center',
						}}
					>
						<ContainerIcon
							sx={({ size, vars }) => ({
								width: size.xS,
								height: size.xS,
								color: vars.palette.uc.highlight.icon.main,
							})}
						/>
						<Typography variant="label-md" fontType="monospace">
							{env.toUpperCase()}
						</Typography>
					</Stack>

					{userEmail && (
						<Typography
							variant="label-md"
							fontType="monospace"
							sx={({ vars }) => ({ color: vars.palette.uc.text.main })}
						>
							user: {userEmail}
						</Typography>
					)}

					{accountNumber && (
						<Typography
							variant="label-md"
							fontType="monospace"
							sx={({ vars }) => ({ color: vars.palette.uc.text.main })}
						>
							acc: {accountNumber}
						</Typography>
					)}

					<Stack
						direction="row"
						sx={{
							gap: 100,
							alignItems: 'center',
						}}
					>
						<Settings2Icon
							sx={({ size, vars }) => ({
								width: size.xS,
								height: size.xS,
								color: vars.palette.uc.text.medium,
							})}
						/>

						<Select
							size="mini"
							value={brandValue}
							onChange={handleBrandChange}
							sx={{ fontFamily: 'monospace' }}
						>
							<MenuItem value="liquidweb">Liquid Web</MenuItem>
							<MenuItem value="nexcess">Nexcess</MenuItem>
						</Select>

						{mode && (
							<Select
								size="mini"
								value={mode}
								onChange={handleThemeChange}
								sx={{ fontFamily: 'monospace' }}
							>
								<MenuItem value="system">System</MenuItem>
								<MenuItem value="light">Light</MenuItem>
								<MenuItem value="dark">Dark</MenuItem>
							</Select>
						)}
					</Stack>

					{/* Feature flags placeholder — coming soon */}
					<Stack
						direction="row"
						sx={{
							gap: 100,
							alignItems: 'center',
							opacity: 0.5,
						}}
					>
						<FlagIcon
							sx={({ size, vars }) => ({
								width: size.xS,
								height: size.xS,
								color: vars.palette.uc.text.medium,
							})}
						/>
						<Box
							sx={({ vars, borderRadius }) => ({
								bgcolor: vars.palette.uc.bg.weak,
								borderRadius: borderRadius.roundedSm,
								px: 100,
								height: 32,
								display: 'flex',
								alignItems: 'center',
							})}
						>
							<Typography variant="label-md" fontType="monospace">
								Coming soon
							</Typography>
						</Box>
					</Stack>

					<Box sx={{ marginLeft: 'auto' }}>
						<IconButton
							variant="secondary"
							size="small"
							aria-label="Toggle React Query devtools"
							onClick={() => setIsDevToolsOpen((v) => !v)}
							sx={({ vars }) => ({
								color: isDevToolsOpen
									? vars.palette.uc.highlight.icon.main
									: vars.palette.uc.text.medium,
							})}
						>
							<ToolboxIcon />
						</IconButton>
					</Box>
				</Stack>
			</Box>
			{/* React Query devtools panel */}
			{isDevToolsOpen && (
				<Box
					sx={({ borderRadius, size }) => ({
						position: 'fixed',
						bottom: size.xL, // toolbar height
						left: 0,
						right: 0,
						zIndex: 1299,
						overflow: 'hidden',
						borderRadius: `${borderRadius.rounded} ${borderRadius.rounded} 0 0`,
					})}
				>
					<ReactQueryDevtoolsPanel
						isOpen={isDevToolsOpen}
						setIsOpen={setIsDevToolsOpen}
						onDragStart={() => {}} // drag disabled — panel is position:fixed
					/>
				</Box>
			)}
		</>
	);
};

export default HeadsUpDisplay;
